import { format, isValid, parse } from "date-fns";

/** Shared schedule bounds and defaults for Remediation Hub forms. */

export const SCHEDULE_MAX_DAYS = 30;

/** Earliest allowed schedule: one minute from now (no pre-execution notice window in UI). */
const MIN_LEAD_MS = 60 * 1000;

export function getScheduleBounds() {
  const now = Date.now();
  const minDt = new Date(now + MIN_LEAD_MS);
  const maxDt = new Date(now + SCHEDULE_MAX_DAYS * 24 * 60 * 60 * 1000);
  return { minDt, maxDt };
}

/** Default pick: next 15-minute slot at least one minute from now. */
export function getDefaultScheduledDate() {
  const { minDt } = getScheduleBounds();
  const def = new Date(minDt);
  def.setSeconds(0, 0);
  const remainder = def.getMinutes() % 15;
  if (remainder !== 0) {
    def.setMinutes(def.getMinutes() + (15 - remainder));
  }
  return def;
}

export function clampScheduledDate(date) {
  if (!date || Number.isNaN(date.getTime())) return null;
  const { minDt, maxDt } = getScheduleBounds();
  if (date < minDt) return minDt;
  if (date > maxDt) return maxDt;
  return date;
}

export function isoToDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_e) {
    return null;
  }
}

export function dateToIso(date) {
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function validateScheduledIso(iso) {
  const d = isoToDate(iso);
  if (!d) return "Pick a date and time.";
  const { minDt, maxDt } = getScheduleBounds();
  if (d < minDt) return "Pick a future date and time.";
  if (d > maxDt) return `Time can't be more than ${SCHEDULE_MAX_DAYS} days out.`;
  return null;
}

export function formatScheduleHint() {
  const { maxDt } = getScheduleBounds();
  return `Type or pick a time in the future, within ${SCHEDULE_MAX_DAYS} days (through ${maxDt.toLocaleString()}).`;
}

export const SCHEDULE_DISPLAY_FORMAT = "MMM d, yyyy h:mm aa";

export function formatScheduleDisplay(iso) {
  const d = isoToDate(iso);
  if (!d) return "";
  return format(d, SCHEDULE_DISPLAY_FORMAT);
}

const TYPED_PARSE_FORMATS = [
  SCHEDULE_DISPLAY_FORMAT,
  "MMM d, yyyy h:mm a",
  "M/d/yyyy h:mm aa",
  "M/d/yyyy h:mm a",
  "yyyy-MM-dd HH:mm",
  "yyyy-MM-dd'T'HH:mm",
];

/** Parse manual input from the Run at field. */
export function parseScheduleInput(text) {
  const raw = (text || "").trim();
  if (!raw) return null;
  for (const fmt of TYPED_PARSE_FORMATS) {
    try {
      const d = parse(raw, fmt, new Date());
      if (isValid(d)) return d;
    } catch (_e) {
      /* try next format */
    }
  }
  const fallback = new Date(raw);
  return isValid(fallback) ? fallback : null;
}
